import React, { useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TableSortLabel,
    TablePagination,
    Chip,
    Box,
    TextField,
    InputAdornment
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { FoodItem } from '../../../services/api';

interface InventoryTableProps {
    inventory: FoodItem[];
}

type SortDirection = 'asc' | 'desc';

interface HeadCell {
    id: keyof FoodItem | 'daysUntilExpiry';
    label: string;
    numeric: boolean;
}

const headCells: HeadCell[] = [
    { id: 'name', label: 'Food Item', numeric: false },
    { id: 'quantity', label: 'Quantity', numeric: true },
    { id: 'location', label: 'Storage Location', numeric: false },
    { id: 'daysUntilExpiry', label: 'Expiry', numeric: true },
    { id: 'storageCondition', label: 'Storage Condition', numeric: false },
];

const InventoryTable: React.FC<InventoryTableProps> = ({ inventory }) => {
    const [order, setOrder] = useState<SortDirection>('asc');
    const [orderBy, setOrderBy] = useState<string>('name');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');

    // Calculate days until expiry for each item
    const inventoryWithDaysUntilExpiry = inventory.map(item => {
        const expiryDate = new Date(item.expiryDate);
        const currentDate = new Date();
        const daysUntilExpiry = Math.floor((expiryDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24));
        return { ...item, daysUntilExpiry };
    });

    // Sort function for inventory items
    const getComparator = (order: SortDirection, orderBy: string) => {
        return order === 'desc'
            ? (a: any, b: any) => descendingComparator(a, b, orderBy)
            : (a: any, b: any) => -descendingComparator(a, b, orderBy);
    };

    const descendingComparator = (a: any, b: any, orderBy: string) => {
        if (b[orderBy] < a[orderBy]) {
            return -1;
        }
        if (b[orderBy] > a[orderBy]) {
            return 1;
        }
        return 0;
    };

    // Handler for sort request
    const handleRequestSort = (property: string) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // Filter inventory based on search term
    const filteredInventory = inventoryWithDaysUntilExpiry.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.storageCondition.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort and paginate inventory
    const sortedInventory = filteredInventory.sort(getComparator(order, orderBy));
    const paginatedInventory = sortedInventory.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    // Handlers for pagination
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Handler for search changes
    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
        setPage(0);
    };

    // Get color for expiry chip
    const getExpiryChipColor = (days: number) => {
        if (days <= 0) return 'error';
        if (days <= 30) return 'warning';
        if (days <= 90) return 'info';
        return 'success';
    };

    // Get label for expiry chip
    const getExpiryLabel = (days: number) => {
        if (days <= 0) return 'Expired';
        if (days === 1) return '1 day left';
        return `${days} days left`;
    };

    return (
        <Box>
            <Box sx={{ mb: 2 }}>
                <TextField
                    fullWidth
                    placeholder="Search by name, location, or storage condition"
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <TableContainer component={Paper}>
                <Table aria-label="food inventory table">
                    <TableHead>
                        <TableRow>
                            {headCells.map((headCell) => (
                                <TableCell
                                    key={headCell.id}
                                    align={headCell.numeric ? 'right' : 'left'}
                                    sortDirection={orderBy === headCell.id ? order : false}
                                >
                                    <TableSortLabel
                                        active={orderBy === headCell.id}
                                        direction={orderBy === headCell.id ? order : 'asc'}
                                        onClick={() => handleRequestSort(headCell.id)}
                                    >
                                        {headCell.label}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedInventory.map((item) => (
                            <TableRow key={item.id}>
                                <TableCell component="th" scope="row">
                                    {item.name}
                                </TableCell>
                                <TableCell align="right">
                                    {item.quantity} {item.unit}
                                </TableCell>
                                <TableCell>{item.location}</TableCell>
                                <TableCell align="right">
                                    <Chip
                                        label={getExpiryLabel(item.daysUntilExpiry)}
                                        color={getExpiryChipColor(item.daysUntilExpiry)}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>{item.storageCondition}</TableCell>
                            </TableRow>
                        ))}
                        {paginatedInventory.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    No inventory items found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredInventory.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Box>
    );
};

export default InventoryTable; 